import { connect, useSelector } from 'react-redux';
import partial from 'transmute/partial';

//@ts-expect-error this file has no types
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { EditIcon } from 'foundations-assets';
import { getCurrentUser, getHasEditAllScope, getHasEditOwnedScope, getHasEditTeamScope, getUserPrimaryTeam } from '../../selectors/authSelectors';
import { getTeamsMap } from '../../selectors/usersSelectors';
import { getOwnerIdToUserIdMap } from '../../selectors/ownersSelectors';
import { getFilters, getTimePeriodFilter } from '../../selectors/filtersSelectors';
import { getIsSinglePipeline } from '../../selectors/filtersSelectors';
import { getFiscalOffset, getStaleSubmissionLimit } from '../../selectors/portalSettings';
import { canUserSubmitForecast } from '../../utils/permissionsHelper';
import { StyledCurrencyWrapper } from '../../utils/StyledCurrencyWrapper';
import { deriveSubmissionStaleness, submissionLimitTimestamp } from '../../utils/dateUtils';
import OnboardingForecastSubmissionTourStep from '../tour/OnboardingForecastSubmissionTourStep';
import ForecastCellTooltip from './ForecastCellTooltip';
import { getForecastedAmount } from '../../utils/submissionUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ForecastCell = ({
  canEditAll,
  canEditOwned,
  canEditTeam,
  currentUser,
  fiscalOffset,
  isSinglePipeline,
  onEditForecastClick,
  ownerIdToUserIdMap,
  primaryTeam,
  rowData,
  staleSubmissionAmount,
  teamsMap
}) => {
  const {
    columnTotal,
    forecastLastModified,
    id,
    isTotalRow,
    isUser,
    name,
    teamSubmissionAmount,
    userSubmissionTotal
  } = rowData;
  const timePeriod = useSelector(getTimePeriodFilter);
  const hasPermissionToSubmit = canUserSubmitForecast({
    canEditAll,
    canEditOwned,
    canEditTeam,
    currentUserId: currentUser.user_id,
    isUser,
    primaryTeam,
    targetEditId: isUser ? ownerIdToUserIdMap.get(id) : id,
    teams: teamsMap
  });
  const showEditIcon = id && !(isUser && isTotalRow);
  const noValueRightMargin = showEditIcon ? '400' : '1000';
  const forecastValueRightPadding = showEditIcon ? '100' : '600';
  const isTeamRow = !isUser && id;
  const valueToDisplay = getForecastedAmount(rowData);

  // This will be true for both zero and non-zero submission values,
  // but false if no submission was made.
  const hasValue = valueToDisplay && valueToDisplay.value || !!forecastLastModified;
  const tooltipTotal = isTotalRow ? columnTotal : userSubmissionTotal;
  const showDateModifiedSubtext = (isSinglePipeline || rowData && rowData.isAllPipelines) && forecastLastModified;
  const isSubmissionStale = deriveSubmissionStaleness(rowData.forecastLastModified, staleSubmissionAmount);
  const cell = /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "end",
    alignItems: "start",
    children: [!hasValue ? /*#__PURE__*/_jsx(ForecastCellTooltip, {
      isTeamRow: isTeamRow,
      isTotalRow: isTotalRow,
      name: name,
      timePeriod: timePeriod,
      rollupAmount: tooltipTotal,
      fiscalOffset: fiscalOffset,
      teamSubmissionAmount: teamSubmissionAmount,
      children: /*#__PURE__*/_jsx(View, {
        as: "span",
        marginInlineEnd: noValueRightMargin,
        "data-test-id": "submission-cell-value",
        children: "\u2014"
      })
    }) : /*#__PURE__*/_jsxs(View, {
      paddingInlineEnd: forecastValueRightPadding,
      children: [/*#__PURE__*/_jsx(ForecastCellTooltip, {
        isTeamRow: isTeamRow,
        isTotalRow: isTotalRow,
        name: name,
        timePeriod: timePeriod,
        rollupAmount: tooltipTotal,
        fiscalOffset: fiscalOffset,
        teamSubmissionAmount: teamSubmissionAmount,
        children: valueToDisplay && /*#__PURE__*/_jsx(StyledCurrencyWrapper, {
          "data-test-id": "submission-cell-value",
          children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
            value: valueToDisplay.value,
            currencyCode: valueToDisplay.currencyCode
          })
        })
      }), showDateModifiedSubtext && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(Tooltip, {
          placement: "bottom",
          disabled: isTotalRow || !isSubmissionStale,
          title: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.forecastCell.tooltip.staleSubmission"
            }), /*#__PURE__*/_jsx(FormattedDuration
            // staleSubmissionAmount exists when `isSubmissionStale` is true
            , {
              to: submissionLimitTimestamp(staleSubmissionAmount),
              from: new Date(),
              includeAffix: true
            })]
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsxs(Text, {
              as: "small",
              children: [!isTotalRow && isSubmissionStale && /*#__PURE__*/_jsx(StatusDot, {
                use: "warning",
                paddingInlineEnd: "0"
              }), /*#__PURE__*/_jsx(FormattedDuration, {
                from: new Date(),
                to: +forecastLastModified,
                includeAffix: true
              })]
            })
          })
        })]
      })]
    }), showEditIcon && /*#__PURE__*/_jsx(IconButton, {
      "data-test-id": "submission-pencil-button",
      tooltip: !hasPermissionToSubmit && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "repDetail.disabledTooltip.permissions"
      }),
      placement: "left bottom",
      use: "link",
      disabled: !hasPermissionToSubmit,
      size: "sm",
      onClick: partial(onEditForecastClick, rowData),
      children: /*#__PURE__*/_jsx(EditIcon, {})
    })]
  });
  if (isTotalRow) {
    return /*#__PURE__*/_jsx(OnboardingForecastSubmissionTourStep, {
      children: cell
    });
  }
  return cell;
};
export default connect(state => ({
  canEditAll: getHasEditAllScope(state),
  canEditOwned: getHasEditOwnedScope(state),
  canEditTeam: getHasEditTeamScope(state),
  currentUser: getCurrentUser(state),
  filters: getFilters(state),
  fiscalOffset: getFiscalOffset(state),
  isSinglePipeline: getIsSinglePipeline(state),
  ownerIdToUserIdMap: getOwnerIdToUserIdMap(state),
  primaryTeam: getUserPrimaryTeam(state),
  staleSubmissionAmount: getStaleSubmissionLimit(state),
  teamsMap: getTeamsMap(state)
}))(ForecastCell);